//
// PROTOTYPE: per-interaction node icons sourced from the WixarVR Studio.
//
// These PNGs were extracted from the Unity studio's authoritative
// `DataManager.GetIconeDependingOnContentType` mapping and renamed to the
// web graph's `modelName` key. They live in `public/interactionIcons/` and
// are served via PUBLIC_URL (no bundler import, easy to remove).
//
// Render-time only: SVGNodeAsset draws these with preserveAspectRatio and
// falls back to the legacy generic glyphs for any modelName not listed here.
//

// The 40 modelNames that have a studio icon staged in public/interactionIcons/.
// (content-scene has no usable studio icon and intentionally keeps the legacy
// generic glyph. The two content-hotspot-scene-* share a dedicated icon
// supplied by Sébastien 2026-07-16 — the studio had no usable one.)
const AVAILABLE_INTERACTION_ICONS = new Set<string>([
  "content-3d-viewer",
  "content-audio",
  "content-avatar-ai",
  "content-avatar-ai-persona",
  "content-avatar-ai-session-debrief",
  "content-avatar-audio-player",
  "content-blur",
  "content-button-1",
  "content-button-2",
  "content-button-reload",
  "content-clean",
  "content-danger-catch",
  "content-dust",
  "content-element-catch",
  "content-fire",
  "content-fire-extinguisher",
  "content-highlight",
  "content-hotspot-scene-image",
  "content-hotspot-scene-video",
  "content-onboarding",
  "content-pdf-viewer",
  "content-photo-catch",
  "content-quizz-1",
  "content-quizz-2",
  "content-quizz-3",
  "content-quizz-image-multiple",
  "content-quizz-sort",
  "content-random-quizz",
  "content-scoring-general",
  "content-scoring-scene",
  "content-speech-to-check",
  "content-text",
  "content-thumbnail",
  "content-tuto",
  "content-tuto2",
  "content-tuto-2",
  "content-video-2-d",
  "content-vr-locator",
  "content-yes-no-question",
  "content-zone-detect",
  "content-zone-sort",
]);

// ContentType.ts accepts both spellings for the 3D viewer; normalize the alias.
const MODEL_NAME_ALIASES: Record<string, string> = {
  "content-3-d-viewer": "content-3d-viewer",
};

// Per-icon scale factor, computed from each PNG's real content bounding box so
// the longest content side occupies ~80% of the icon box. This harmonizes the
// apparent size across icons that have very different intrinsic padding/aspect
// (e.g. the slim "!" of danger-catch vs. the full-bleed thumbnail frame).
// Render-time only — the source PNGs are untouched.
const INTERACTION_ICON_SCALE: Record<string, number> = {
  "content-3d-viewer": 0.83,
  "content-audio": 0.83,
  "content-avatar-ai": 0.9,
  "content-avatar-ai-persona": 0.81,
  "content-avatar-ai-session-debrief": 0.83,
  "content-avatar-audio-player": 0.83,
  "content-blur": 0.98,
  "content-button-1": 0.92,
  "content-button-2": 0.97,
  "content-button-reload": 0.92,
  "content-clean": 0.81,
  "content-danger-catch": 1.28,
  "content-dust": 0.8,
  "content-element-catch": 0.8,
  "content-fire": 0.97,
  "content-fire-extinguisher": 0.82,
  "content-highlight": 0.8,
  "content-hotspot-scene-image": 0.83,
  "content-hotspot-scene-video": 0.83,
  "content-onboarding": 0.84,
  "content-pdf-viewer": 0.91,
  "content-photo-catch": 0.85,
  "content-quizz-1": 0.8,
  "content-quizz-2": 0.85,
  "content-quizz-3": 0.8,
  "content-quizz-image-multiple": 0.85,
  "content-quizz-sort": 0.8,
  "content-random-quizz": 0.8,
  "content-scoring-general": 0.86,
  "content-scoring-scene": 0.87,
  "content-speech-to-check": 0.8,
  "content-text": 0.92,
  "content-thumbnail": 1.0,
  "content-tuto": 0.91,
  "content-tuto2": 0.91,
  "content-tuto-2": 0.91,
  "content-video-2-d": 0.82,
  "content-vr-locator": 0.89,
  "content-yes-no-question": 0.8,
  "content-zone-detect": 0.86,
  "content-zone-sort": 0.86,
};

export type InteractionIcon = { url: string; scale: number };

/**
 * Returns the studio icon (public URL + harmonizing scale) for a given content
 * modelName, or null when there is no mapped icon (caller should fall back to
 * the legacy generic glyph).
 */
export const getInteractionIcon = (modelName?: string): InteractionIcon | null => {
  if (!modelName) return null;
  const resolved = MODEL_NAME_ALIASES[modelName] ?? modelName;
  if (!AVAILABLE_INTERACTION_ICONS.has(resolved)) return null;
  return {
    // BASE_URL carries its own trailing slash ("/"), unlike CRA's PUBLIC_URL ("").
    url: `${import.meta.env.BASE_URL}interactionIcons/${resolved}.png`,
    scale: INTERACTION_ICON_SCALE[resolved] ?? 0.85,
  };
};
